import React from "react";
import { useField } from "formik";
import { Dropdown, IDropdownOption, TextField, ITextFieldStyles, ITheme, mergeStyles } from "office-ui-fabric-react";
import { FluentCustomizations } from "@uifabric/fluent-theme";
import { textfieldS } from "../../styles/formField.styles";

export const PHONE_PREFIXES: IDropdownOption[] = [
  { key: "+48", text: "+48 (PL)" },
  { key: "+49", text: "+49 (DE)" },
  { key: "+420", text: "+420 (CZ)" },
  { key: "+421", text: "+421 (SK)" },
  { key: "+372", text: "+372 (ET)" },
];

export const ADMIN_PHONE_PREFIXES: IDropdownOption[] = [
  { key: "+48", text: "+48 (PL)" },
  { key: "+370", text: "+370 (LT)" },
];

const LANG_TO_PREFIX: Record<string, string> = {
  pl: "+48",
  de: "+49",
  sk: "+421",
  et: "+372",
  cs: "+420",
  lt: "+370",
};

export const defaultPrefix = (
  language: string,
  prefixes: IDropdownOption[] = PHONE_PREFIXES,
): string => {
  const languagePrefix = LANG_TO_PREFIX[language];
  return prefixes.some(p => p.key === languagePrefix) ? languagePrefix : prefixes[0].key as string;
};

export const splitStoredPhone = (
  phone: string,
  language: string,
  prefixes: IDropdownOption[] = PHONE_PREFIXES,
): { prefix: string; number: string } => {
  const sortedPrefixes = prefixes.map(p => p.key as string).sort((a, b) => b.length - a.length);
  for (const p of sortedPrefixes) {
    if (phone && phone.startsWith(p)) {
      return { prefix: p, number: phone.slice(p.length) };
    }
  }
  return { prefix: defaultPrefix(language, prefixes), number: phone || "" };
};

const theme: ITheme = FluentCustomizations.settings.theme;

const errorTextClass = mergeStyles(theme.fonts.small, {
  color: theme.semanticColors.errorText,
  margin: 0,
  paddingTop: 5,
  display: "flex",
  alignItems: "center",
});

const numberFieldS: Partial<ITextFieldStyles> = {
  root: { flex: 1, minWidth: 0 },
};

const errorBorder = {
  borderColor: theme.semanticColors.errorText,
  selectors: { ":hover": { borderColor: theme.semanticColors.errorText } },
};

const numberFieldErrorS: Partial<ITextFieldStyles> = {
  root: { flex: 1, minWidth: 0 },
  fieldGroup: errorBorder,
};

const labelStyle: React.CSSProperties = {
  flex: "0 1 200px",
  marginRight: 30,
  fontSize: 16,
  fontWeight: 600,
  paddingTop: 5,
};

interface PhonePrefixFieldProps {
  prefixName: string;
  numberName: string;
  label: string;
  required?: boolean;
  prefixOptions?: IDropdownOption[];
  textStyles?: Partial<ITextFieldStyles>;
  horizontal?: boolean;
}

const PhonePrefixField: React.FC<PhonePrefixFieldProps> = ({
  prefixName, numberName, label, required,
  prefixOptions = PHONE_PREFIXES,
  textStyles = textfieldS,
  horizontal = false,
}) => {
  const [, { value: prefixValue }, { setValue: setPrefix }] = useField(prefixName);
  const [numberField, { value: numberValue, error, touched }] = useField(numberName);
  const visibleError = touched && error ? error : "";
  const errorText = visibleError && (
    <div role="alert">
      <p className={errorTextClass}>
        <span>{visibleError}</span>
      </p>
    </div>
  );

  if (horizontal) {
    return (
      <div style={{ paddingTop: 30, width: "100%" }}>
        <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-start" }}>
          <span style={labelStyle}>{label}</span>
          <div style={{ display: "flex", flex: "0 1 350px", gap: 8, alignItems: "flex-start", minWidth: 0 }}>
            <div style={{ width: 130, flexShrink: 0 }}>
              <Dropdown
                options={prefixOptions}
                selectedKey={prefixValue}
                onChange={(_, option) => option && setPrefix(option.key)}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <TextField
                styles={visibleError ? numberFieldErrorS : numberFieldS}
                {...numberField}
                value={numberValue}
              />
            </div>
          </div>
        </div>
        {errorText}
      </div>
    );
  }

  const verticalNumberS: Partial<ITextFieldStyles> = visibleError
    ? { ...textStyles, root: [textStyles.root, { marginBottom: 0 }], fieldGroup: errorBorder }
    : textStyles;

  return (
    <div style={{ marginBottom: visibleError ? 16 : 0 }}>
      <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
        <div style={{ width: "130px", flexShrink: 0 }}>
          <Dropdown
            label={label}
            options={prefixOptions}
            selectedKey={prefixValue}
            onChange={(_, option) => option && setPrefix(option.key)}
            required={required}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <TextField
            label={" "}
            styles={verticalNumberS}
            {...numberField}
            value={numberValue}
          />
        </div>
      </div>
      {errorText}
    </div>
  );
};

export default PhonePrefixField;
